import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Bootstrap } from './api'
import type { Household, Member, SessionUser } from '../../shared/types'

type AuthState = 'loading' | 'anonymous' | 'signed_in'

interface SessionValue {
  state: AuthState
  household: Household | null
  householdName: string | null
  members: Member[]
  session: (SessionUser & { hasAccount: boolean }) | null
  isParent: boolean
  viewedMemberId: string | null
  viewedMember: Member | null
  setViewedMemberId: (id: string | null) => void
  refresh: () => Promise<void>
  /** Ends the session. The next person signs in with their own login. */
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [override, setOverride] = useState<string | null>(null)

  const { data, isLoading } = useQuery<Bootstrap>({
    queryKey: ['bootstrap'],
    queryFn: api.bootstrap,
    staleTime: 30_000,
  })

  const value = useMemo<SessionValue>(() => {
    const refresh = async (): Promise<void> => {
      await queryClient.invalidateQueries()
    }

    // Drops everything the previous person could see, then reloads who (if
    // anyone) this device is now. Bootstrap is refetched rather than removed:
    // removing a query that is still being watched leaves the screen showing
    // the old person until something happens to re-render.
    const resetToBootstrap = async (): Promise<void> => {
      setOverride(null)
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'bootstrap' })
      await queryClient.refetchQueries({ queryKey: ['bootstrap'] })
    }

    const signOut = async (): Promise<void> => {
      await api.logout()
      await resetToBootstrap()
    }

    const base = {
      household: null,
      householdName: null,
      members: [] as Member[],
      session: null,
      isParent: false,
      viewedMemberId: null,
      viewedMember: null,
      setViewedMemberId: setOverride,
      refresh,
      signOut,
    }

    if (isLoading || !data) return { ...base, state: 'loading' as const }

    if (data.state === 'anonymous') return { ...base, state: 'anonymous' as const }

    const isParent = data.session.role === 'parent'
    // A kid's override is ignored; the server would refuse it anyway.
    const viewedMemberId = isParent ? (override ?? data.session.memberId) : data.session.memberId
    const viewedMember = data.members.find((m) => m.id === viewedMemberId) ?? null

    return {
      ...base,
      state: 'signed_in' as const,
      household: data.household,
      householdName: data.household?.name ?? null,
      members: data.members,
      session: data.session,
      isParent,
      viewedMemberId,
      viewedMember,
    }
  }, [isLoading, data, override, queryClient])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession must be used inside SessionProvider')
  return value
}
